import React, { useState, useEffect } from 'react';
import { FaTimes, FaDownload, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { exportWorkerProfileToPDF } from '../utils/pdfExport';
import { toast } from 'react-hot-toast';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  userId?: string;
  language: 'es' | 'en';
}

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  profile,
  userId,
  language
}) => {
  const { t } = useTranslation();
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Función para generar QR code
  const generateQRCode = async (text: string): Promise<string> => {
    try {
      console.log('Generating QR code for text:', text);
      const qrDataUrl = await QRCode.toDataURL(text, {
        width: 80,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      console.log('QR code generated successfully');
      return qrDataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  useEffect(() => {
    if (isOpen && profile) {
      generatePDFPreview();
    }
  }, [isOpen, profile]);

  const generatePDFPreview = async () => {
    if (!profile) return;

    try {
      setIsGenerating(true);
      setPdfPages([]);
      setCurrentPage(0);

      // Usar QR code guardado en Firestore o generar uno nuevo
      let qrCodeDataUrl = profile.qrCodeUrl;
      
      if (!qrCodeDataUrl) {
        // Generar QR code para el perfil público
        const finalUserId = userId || profile.id || profile.uid || (profile as any).userId || 'profile';
        const publicProfileUrl = `https://brix-v2.web.app/worker/${finalUserId}`;
        console.log('Generating QR for URL:', publicProfileUrl);
        qrCodeDataUrl = await generateQRCode(publicProfileUrl);
        console.log('QR Code generated:', qrCodeDataUrl ? 'Success' : 'Failed');
      }

      // Importar las funciones necesarias desde pdfExport
      const { generateHeaderHTML, generateContentHTML, generateFooterHTML, generateCertificationsPageHTML } = await import('../utils/pdfExport');
      
      // Generar la primera página con información del perfil
      const firstPageHTML = `
        <div style="font-family: Montserrat, Arial, sans-serif; color: #1f2937; max-width: 794px; margin: 0 auto;">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
          </style>
          
          <!-- ENCABEZADO: Logo de Brix -->
          ${generateHeaderHTML()}
          
          <!-- CONTENIDO: Información del usuario -->
          ${generateContentHTML(profile, qrCodeDataUrl, language)}
          
          <!-- PIE DE PÁGINA: Texto adaptativo según idioma -->
          ${generateFooterHTML(language)}
        </div>
      `;
      
      // Generar la segunda página con certificaciones
      const secondPageHTML = generateCertificationsPageHTML(profile, language);
      
      // Crear contenedores temporales para cada página
      const firstPageContainer = document.createElement('div');
      firstPageContainer.style.position = 'absolute';
      firstPageContainer.style.left = '-9999px';
      firstPageContainer.style.top = '0';
      firstPageContainer.style.width = '794px';
      firstPageContainer.style.backgroundColor = 'white';
      firstPageContainer.style.padding = '40px';
      firstPageContainer.style.fontFamily = 'Montserrat, Arial, sans-serif';
      firstPageContainer.style.lineHeight = '1.5';
      firstPageContainer.style.color = '#1f2937';
      firstPageContainer.style.fontSize = '14px';
      
      const secondPageContainer = document.createElement('div');
      secondPageContainer.style.position = 'absolute';
      secondPageContainer.style.left = '-9999px';
      secondPageContainer.style.top = '0';
      secondPageContainer.style.width = '794px';
      secondPageContainer.style.backgroundColor = 'white';
      secondPageContainer.style.padding = '40px';
      secondPageContainer.style.fontFamily = 'Montserrat, Arial, sans-serif';
      secondPageContainer.style.lineHeight = '1.5';
      secondPageContainer.style.color = '#1f2937';
      secondPageContainer.style.fontSize = '14px';
      
      // Configurar contenido de las páginas
      firstPageContainer.innerHTML = firstPageHTML;
      secondPageContainer.innerHTML = secondPageHTML;
      
      // Agregar contenedores al DOM
      document.body.appendChild(firstPageContainer);
      document.body.appendChild(secondPageContainer);
      
      // Esperar a que el contenido se renderice
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Capturar la primera página
      const firstPageCanvas = await html2canvas(firstPageContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        scrollX: 0,
        scrollY: 0,
        logging: false
      });
      
      // Capturar la segunda página
      const secondPageCanvas = await html2canvas(secondPageContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        scrollX: 0,
        scrollY: 0,
        logging: false
      });
      
      // Convertir canvas a URLs de datos
      const firstPageDataUrl = firstPageCanvas.toDataURL('image/png', 1.0);
      const secondPageDataUrl = secondPageCanvas.toDataURL('image/png', 1.0);
      
      // Establecer las páginas
      setPdfPages([firstPageDataUrl, secondPageDataUrl]);
      
      // Limpiar los contenedores temporales
      document.body.removeChild(firstPageContainer);
      document.body.removeChild(secondPageContainer);
      
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      toast.error(t('profile.error.pdfGeneration'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!pdfPages.length) return;

    try {
      setIsDownloading(true);
      await exportWorkerProfileToPDF(profile, language);
      toast.success(t('profile.success.pdfGenerated'));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('profile.error.pdfGeneration'));
    } finally {
      setIsDownloading(false);
    }
  };

  const nextPage = () => {
    if (currentPage < pdfPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('profile.viewPDF')} - {profile?.firstName} {profile?.lastName}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={!pdfPages.length || isDownloading}
              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaDownload />
              )}
              <span>{isDownloading ? t('common.loading') : t('common.download')}</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isGenerating ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FaSpinner className="animate-spin text-red-600 text-4xl mx-auto mb-4" />
                <p className="text-gray-600">{t('common.loading')}</p>
              </div>
            </div>
          ) : pdfPages.length > 0 ? (
            <div className="w-full">
              {/* Navegación de páginas */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 0}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft />
                  <span>Anterior</span>
                </button>
                
                <span className="text-sm text-gray-600">
                  Página {currentPage + 1} de {pdfPages.length}
                </span>
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === pdfPages.length - 1}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Siguiente</span>
                  <FaChevronRight />
                </button>
              </div>
              
              {/* Vista previa de la página actual */}
              <div className="w-full border border-gray-200 rounded-lg">
                <img
                  src={pdfPages[currentPage]}
                  alt={`PDF Preview - Página ${currentPage + 1}`}
                  className="w-full h-auto"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-600">{t('profile.error.pdfGeneration')}</p>
                <button
                  onClick={generatePDFPreview}
                  className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('common.retry')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal; 